import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Copy, Trash2, UploadCloud, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/fileshare/components/ui/button";
import { Skeleton } from "@/fileshare/components/ui/skeleton";
import { formatBytes, timeLeft } from "@/fileshare/lib/format";

const SKELETON_ROWS = ["row-a", "row-b", "row-c", "row-d"];

export default function FilesTable({ files, loading, page, pages, onPageChange, onCopy, onDelete }) {
  return (
    <div className="mt-8 bg-white border border-slate-200 rounded-lg overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100">
        <h2 className="text-sm font-semibold text-slate-900">Your files</h2>
      </div>
      {loading ? (
        <div className="p-5 space-y-3">{SKELETON_ROWS.map((k) => <Skeleton key={k} className="h-10 rounded-md" />)}</div>
      ) : files.length === 0 ? (
        <div className="p-12 text-center">
          <UploadCloud className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">No files yet. Upload your first file to get started.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="files-table">
            <thead>
              <tr className="border-b border-slate-100 text-left">
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">File</th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Size</th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Downloads</th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Expires</th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {files.map((f, i) => (
                <motion.tr
                  key={f.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                  className="border-b border-slate-50 hover:bg-slate-50/50"
                  data-testid={`file-row-${f.id}`}
                >
                  <td className="px-5 py-3.5">
                    <Link to={`/d/${f.slug}`} className="text-sm font-medium text-slate-900 hover:text-blue-500 truncate block max-w-[220px]">
                      {f.original_filename}
                    </Link>
                    <span className="text-xs text-slate-400">/d/{f.slug}{!f.is_active && " · inactive"}</span>
                  </td>
                  <td className="px-5 py-3.5 text-sm text-slate-600">{formatBytes(f.file_size)}</td>
                  <td className="px-5 py-3.5 text-sm text-slate-600">{f.download_count}{f.max_downloads != null ? `/${f.max_downloads}` : ""}</td>
                  <td className="px-5 py-3.5 text-sm text-slate-600">{f.expires_at ? timeLeft(f.expires_at) : "Never"}</td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-1">
                      <Button data-testid={`copy-${f.id}`} variant="ghost" size="icon" onClick={() => onCopy(f.slug)} className="text-slate-400 hover:text-blue-500 h-8 w-8"><Copy className="h-4 w-4" /></Button>
                      <Button data-testid={`delete-${f.id}`} variant="ghost" size="icon" onClick={() => onDelete(f.id)} className="text-slate-400 hover:text-red-500 h-8 w-8"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {pages > 1 && (
        <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100">
          <span className="text-xs text-slate-500">Page {page} of {pages}</span>
          <div className="flex gap-1">
            <Button variant="outline" size="icon" className="h-8 w-8" disabled={page <= 1} onClick={() => onPageChange(page - 1)}><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="outline" size="icon" className="h-8 w-8" disabled={page >= pages} onClick={() => onPageChange(page + 1)}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
    </div>
  );
}
